import * as THREE from 'three';
import SpriteText from 'three-spritetext';
import TWEEN from '@tweenjs/tween.js';
import moment from 'moment';
import game from '@/model/game';
import PianoUtil from '@/util/piano-util';
import SpriteAnimation from '@/components/three/sprite-animation';
const TEXTURE_URL = 'public/images/GH3_PC-Slash.webp';
// import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
// import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { UNITS_PER_SECOND } from '@/components/engine-3d';
const DEBUG = false;
const SHOW_GRID = true;
const KEY_WIDTH = 15, PADDING = 2, GEM_HEIGHT = 10;
const GEM_COLOR_BLACK = game.settings.blackGemColor, GEM_COLOR_WHITE = game.settings.whiteGemColor;

export const LINE_MATERIAL_1 = new THREE.LineDashedMaterial(
    { color: game.settings.lineMaterialColor1 }
);
export const LINE_MATERIAL_2 = new THREE.LineDashedMaterial(
    { color: game.settings.lineMaterialColor2 }
);

export function buildHighway({ duration, bpm }, { min, max }) {
    const highwayMeshes = [];
    const texture = new THREE.TextureLoader().load(TEXTURE_URL);
    const noteHighwayMaterial = new THREE.MeshBasicMaterial(
        { map: texture }
    );

    const group = new THREE.Group();
    // BPM is quarter notes per minute
    // one highway section is 8 beats
    const BEATS = 8;
    const BEATS_PER_SECOND = (bpm / 60);
    const ONE_BEAT_IN_SEC = 1 / BEATS_PER_SECOND;
    const SECTION_LENGTH_SECONDS = ONE_BEAT_IN_SEC * BEATS;
    const sections = Math.floor(duration / SECTION_LENGTH_SECONDS) + 3;
    const length = SECTION_LENGTH_SECONDS * UNITS_PER_SECOND;

    for (let n = 0; n < sections; n++) {
        if (DEBUG) {
            // debug time label
            let text = new SpriteText(
                moment(0).seconds(Math.ceil(n * 4)).format('HH:mm:ss'), 12, 'red'
            );
            text.backgroundColor = 'yellow';
            text.position.set(-200, n * 4 * UNITS_PER_SECOND, -40);
            group.add(text);
        }
        const width = (KEY_WIDTH + PADDING) * (PianoUtil.whiteKeysBetween(min, max) + 2);
        group.width = width;
        let geometry = new THREE.BoxGeometry(
            width, length, 20
        );
        const meshContainer = new THREE.Group();
        let mesh = new THREE.Mesh(geometry, noteHighwayMaterial);
        mesh.position.set(-10, length * n, -55);
        meshContainer.add(mesh);

        if (SHOW_GRID) {
            // drawing a grid
            // horizontal
            for (let j = 0; j < BEATS; j++) {
                const lineMaterial = LINE_MATERIAL_1;
                const points = [];
                points.push(new THREE.Vector3(
                    0 - width / 2,
                    j * length / BEATS + n * length,
                    -35));
                points.push(new THREE.Vector3(
                    width / 2 - KEY_WIDTH * 1.5,
                    j * length / BEATS + n * length,
                    -35));
                const geometry = new THREE.BufferGeometry().setFromPoints(points);
                const line = new THREE.Line(geometry, lineMaterial);
                meshContainer.add(line);
                // text marker with time, every 2 lines
                // if (j % 2 === 0) {
                //     const positionTime = (n * SECTION_LENGTH_SECONDS + SECTION_LENGTH_SECONDS / BEATS * j).toFixed(2);
                //     const positionY = j * length / BEATS + n * length;
                //     let label = new SpriteText(
                //          moment(0).seconds(positionTime).format('HH:mm:ss'), 12, 'red'
                //     );
                //     label.backgroundColor = 'yellow';
                //     label.position.set(
                //         0 - width / 2,
                //         j * length / BEATS + n * length,
                //         -35
                //     );
                //     meshContainer.add(label);
                // }
            }
            // vertical
            const bounds = PianoUtil.whiteKeysBetween(min, max) + 1;

            for (let j = 0; j < bounds; j++) {
                const lineMaterial = LINE_MATERIAL_2;
                const points = [];
                points.push(new THREE.Vector3(
                    (j * (KEY_WIDTH + PADDING)) + KEY_WIDTH/2 - width / 2,
                    0 + n * length,
                    -35
                ));
                points.push(new THREE.Vector3(
                    (j * (KEY_WIDTH + PADDING)) + KEY_WIDTH/2 - width / 2,
                    length + n * length,
                    -35
                ));
                const geometry = new THREE.BufferGeometry().setFromPoints(points);
                const line = new THREE.Line(geometry, lineMaterial);
                meshContainer.add(line);
            }
        }
        highwayMeshes.push(meshContainer);
        group.add(meshContainer);
    }
    return group;
}

export function buildGem(note, { min, max }) {
    const group = new THREE.Group();
    // init mesh
    // const isSharp = note.name.includes('#');
    // const isFlat = note.name.includes('b');
    const isBlackNote = PianoUtil.isBlackKey(note.pianoNote);
    let geometry = new THREE.BoxGeometry(isBlackNote ? KEY_WIDTH * 0.75 : KEY_WIDTH, GEM_HEIGHT, 15);
    let material = new THREE.MeshBasicMaterial(
        {
            wireframe: false,
            color: isBlackNote
                ? GEM_COLOR_BLACK
                : GEM_COLOR_WHITE
        }
    );

    let mesh = new THREE.Mesh(geometry, material);
    const OFFSET = (KEY_WIDTH + PADDING) * PianoUtil.whiteKeysBetween(min, max) / 2;
    const width = KEY_WIDTH + PADDING;
    group.add(mesh);
    const whiteKeysNumber = PianoUtil.whiteKeysBetween(min, note.pianoNote);
    group.position.set(
        (whiteKeysNumber - 1) * width - OFFSET,
        note.timeSeconds * UNITS_PER_SECOND + GEM_HEIGHT * 0.5,
        -40);
    if (isBlackNote) {
        group.position.x -= KEY_WIDTH / 2;
    }
    // sustain line
    let sustainGeometry = new THREE.BoxGeometry(
        KEY_WIDTH / 8,
        note.durationSeconds * UNITS_PER_SECOND,
        15 / 8
    );
    let sustainMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
    let sustainMesh = new THREE.Mesh(sustainGeometry, sustainMaterial);
    sustainMesh.position.y += note.duration * UNITS_PER_SECOND / 2;

    group.add(sustainMesh);
    group.note = note;

    if (DEBUG === true) {
        let text = new SpriteText(
            note.name, 8, 'red'
        );
        text.backgroundColor = 'yellow';
        text.position.z = 12;
        text.position.x = 20;
        group.add(text);
    }

    const flame = SpriteAnimation.build('public/images/fire1_64.png');
    flame.position.z = 10;
    flame.position.y = 0;
    flame.position.x = 0;
    group.add(flame);
    group.flame = flame;
    flame.visible = false;
    return group;
}

export function animateNoteOn(gem, time = 400) {
    const params = {
        scale: 1,
        color: 0xFFFFFF
    }, paramsTo = {
        scale: 1.4,
        color: 0x00fc00
    };
    return new Promise((res) => {
        new TWEEN.Tween(params)
            .to(paramsTo, time)
            .easing(TWEEN.Easing.Bounce.InOut) // Use an easing function to make the animation smooth.
            .onUpdate(() => {
                gem.scale.set(params.scale, params.scale);
                gem.children[0].material.color = new THREE.Color(params.color);
                gem.children[1].material.color = new THREE.Color(params.color);
            }).start()
            .onComplete(() => {
                res();
            });
    })
}

export function animateNoteMiss(gem, time = 300) {
    const params = {
        scale: 1,
        color: 0xFFFFFF
    }, paramsTo = {
        scale: 0.5,
        color: 0xFF
    };
    return new Promise((res) => {
        new TWEEN.Tween(params)
            .to(paramsTo, time)
            .easing(TWEEN.Easing.Bounce.InOut) // Use an easing function to make the animation smooth.
            .onUpdate(() => {
                gem.scale.set(params.scale, params.scale);
                gem.children[0].material.color = new THREE.Color(params.color);
                gem.children[1].material.color = new THREE.Color(params.color);
            }).start().onComplete(() => {
                res();
            });
    })
}

export function animateNoteOff(gem) {
    return new Promise((resolve, reject) => {
        const intervalId = setInterval(() => {
            gem.scale.x += gem.scale.x > 0 ? -0.05 : 0;
            gem.scale.y += gem.scale.y > 0 ? -0.05 : 0;
        }, 50);
        setTimeout(() => {
            clearInterval(intervalId);
            resolve();
        }, 200)
    })
}

export function buildGems(notes, { min, max }) {
    return notes.map(note => buildGem(note, { min, max }));
}